/*
 * Collection grid: one card per option1 value, plus add-to-cart straight from the card.
 *
 * Only runs inside a #product-grid carrying [data-variant-cards], which the collection
 * section emits solely when its `enable_variant_cards` setting is on. Collections without
 * that setting never load this file and are completely unaffected.
 *
 * Grouping is by option POSITION (option1), never by option name - names vary per product
 * across this store ("Packs", "Pack", "PACK", "Quantity", ...) so matching on them breaks
 * silently the moment someone renames an option in Admin.
 */
(function () {
  'use strict';

  var PROCESSED = 'data-variant-cards-processed';
  var productCache = {};
  var inFlightCards = new WeakSet();

  function moneyFormat(grid) {
    return grid.getAttribute('data-money-format') || '₹{{amount}}';
  }

  function formatMoney(cents, grid) {
    if (window.Shopify && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents, moneyFormat(grid));
    }
    return '₹' + (cents / 100).toFixed(2);
  }

  function optionValue(variant, position) {
    return variant['option' + position];
  }

  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .trim();
  }

  /*
   * Splitting is only meaningful when option1 is an axis worth showing as its own card -
   * the flavor axis, or the colorway on apparel. Plenty of products in this catalogue use
   * option1 for something else entirely - Pack, Packs, PACK, Quantity, Size, Accessory
   * size - and splitting those would produce nonsense cards like "Product name: Pack of 6"
   * instead of one normal product card.
   *
   * This is the one place a name is consulted, and only to decide ELIGIBILITY. Once a
   * product qualifies, every role below is still derived from option position, never name.
   * The expected names come from a section setting, so adding an axis is a theme-editor
   * change rather than a code change.
   */
  function firstOptionName(product) {
    var first = product.options && product.options[0];
    if (!first) return '';
    // /products/x.js returns option objects; some contexts return plain name strings.
    return typeof first === 'string' ? first : first.name;
  }

  // Only the fallback for a section that never emitted the attribute. The real value is
  // the theme-editor setting, so which axes split is a merchandising decision made per
  // collection in the editor, not something to change here.
  var DEFAULT_SPLIT_OPTIONS = 'Pet food flavor';

  // The setting holds a comma-separated list so more than one option name can qualify
  // ("Pet food flavor" for food, "Color" for apparel) without touching this file again.
  function expectedOptionNames(grid) {
    var raw = grid.getAttribute('data-variant-cards-option');
    if (raw === null || raw === '') raw = DEFAULT_SPLIT_OPTIONS;
    var out = [];
    var parts = String(raw).split(',');
    for (var i = 0; i < parts.length; i++) {
      var name = normalise(parts[i]);
      if (name && out.indexOf(name) === -1) out.push(name);
    }
    return out;
  }

  function isSplittable(product, grid) {
    var expected = expectedOptionNames(grid);
    // Preserved from the single-name version: a setting that normalises away to nothing
    // means "no name filter", so every product splits on option1.
    if (!expected.length) return true;
    return expected.indexOf(normalise(firstOptionName(product))) !== -1;
  }

  // Card title reads "Product name: Flavor" so each split card names the product it came
  // from before the option value that distinguishes it.
  function cardLabel(product, variant) {
    return product.title + ': ' + optionValue(variant, 1);
  }

  /*
   * Groups variants by their option1 value, preserving the order Shopify returns them in
   * so "first" means the same thing it does everywhere else in the theme. Groups with no
   * in-stock variant are dropped entirely rather than rendered as a sold-out card.
   */
  function groupByFirstOption(product) {
    var order = [];
    var groups = {};

    product.variants.forEach(function (variant) {
      var key = optionValue(variant, 1);
      if (key == null) return;
      if (!groups[key]) {
        groups[key] = [];
        order.push(key);
      }
      groups[key].push(variant);
    });

    return order
      .map(function (key) {
        var available = groups[key].filter(function (v) {
          return v.available;
        });
        return available.length ? { value: key, representative: available[0] } : null;
      })
      .filter(Boolean);
  }

  function imageForVariant(product, variant) {
    if (variant.featured_image && variant.featured_image.src) return variant.featured_image.src;
    return product.featured_image || null;
  }

  // Shopify image URLs take sizing via a `width` query param; keep any existing params.
  function sizedImage(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function setCardImage(card, product, variant) {
    var src = imageForVariant(product, variant);
    if (!src) return;
    var img = card.querySelector('.card__media img, .card__inner img');
    if (!img) return;

    img.setAttribute('src', sizedImage(src, 533));
    img.setAttribute(
      'srcset',
      [165, 360, 533, 720, 940]
        .map(function (w) {
          return sizedImage(src, w) + ' ' + w + 'w';
        })
        .join(', ')
    );
    img.setAttribute('alt', cardLabel(product, variant));

    // Secondary (hover) image would still show the previous group's photo.
    var images = card.querySelectorAll('.card__media img, .card__inner img');
    for (var i = 1; i < images.length; i++) images[i].remove();
  }

  function setCardPrice(card, variant, grid) {
    var container = card.querySelector('.price');
    if (!container) return;

    var onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
    container.classList.toggle('price--on-sale', !!onSale);

    var regular = container.querySelectorAll('.price__regular .price-item--regular');
    for (var i = 0; i < regular.length; i++) regular[i].textContent = formatMoney(variant.price, grid);

    var sale = container.querySelector('.price__sale .price-item--sale');
    if (sale) sale.textContent = formatMoney(variant.price, grid);

    var struck = container.querySelector('.price__sale .price-item--regular');
    if (struck) struck.textContent = onSale ? formatMoney(variant.compare_at_price, grid) : '';
  }

  function setCardLinks(card, product, variant) {
    var href = product.url + '?variant=' + variant.id;
    var links = card.querySelectorAll('a[href]');
    for (var i = 0; i < links.length; i++) links[i].setAttribute('href', href);
  }

  function setCardTitle(card, product, variant) {
    var label = cardLabel(product, variant);
    var headings = card.querySelectorAll('.card__heading a');
    for (var i = 0; i < headings.length; i++) headings[i].textContent = label;
  }

  /*
   * IDs inside a cloned card would otherwise be duplicated across every split card, which
   * breaks the aria-labelledby references Dawn puts on card links. Re-suffix them per group.
   */
  function uniquifyIds(card, suffix) {
    var withIds = card.querySelectorAll('[id]');
    var remap = {};
    for (var i = 0; i < withIds.length; i++) {
      var oldId = withIds[i].id;
      var newId = oldId + '-' + suffix;
      remap[oldId] = newId;
      withIds[i].id = newId;
    }
    // Attributes that reference ids by bare name, space separated.
    ['aria-labelledby', 'aria-controls', 'aria-describedby'].forEach(function (attribute) {
      var nodes = card.querySelectorAll('[' + attribute + ']');
      for (var j = 0; j < nodes.length; j++) {
        var mapped = nodes[j]
          .getAttribute(attribute)
          .split(/\s+/)
          .map(function (ref) {
            return remap[ref] || ref;
          })
          .join(' ');
        nodes[j].setAttribute(attribute, mapped);
      }
    });

    /*
     * Dawn's quick-add opener points at its modal with a CSS selector (data-modal="#QuickAdd-123").
     * The modal's own id was just re-suffixed above, so without remapping this too every split
     * card would open the same first modal - or none at all.
     */
    var openers = card.querySelectorAll('[data-modal]');
    for (var k = 0; k < openers.length; k++) {
      var selector = openers[k].getAttribute('data-modal');
      if (selector && selector.charAt(0) === '#') {
        var target = remap[selector.slice(1)];
        if (target) openers[k].setAttribute('data-modal', '#' + target);
      }
    }
  }

  /*
   * Dawn's quick-add loads whatever `data-product-url` points at, which is the bare product
   * URL - so every split card would open the product's default flavor instead of its own.
   * Point each card's opener at its representative variant.
   */
  function setQuickAddTarget(card, product, variant) {
    var href = product.url + '?variant=' + variant.id;
    var nodes = card.querySelectorAll('[data-product-url]');
    for (var i = 0; i < nodes.length; i++) nodes[i].setAttribute('data-product-url', href);

    // Single-variant quick-add posts a hidden variant id straight to the cart.
    var idInputs = card.querySelectorAll('input[name="id"], .product-variant-id');
    for (var j = 0; j < idInputs.length; j++) idInputs[j].value = variant.id;
  }

  function buildAddButton(product, variant, grid) {
    var wrap = document.createElement('div');
    wrap.className = 'variant-card__actions';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'variant-card__add button button--full-width button--primary';
    button.setAttribute('data-variant-card-add', '');
    button.setAttribute('data-variant-id', String(variant.id));
    button.setAttribute(
      'aria-label',
      (grid.getAttribute('data-add-to-cart-label') || 'Add to cart') + ': ' + cardLabel(product, variant)
    );

    var label = document.createElement('span');
    label.textContent = grid.getAttribute('data-add-to-cart-label') || 'Add to cart';
    button.appendChild(label);

    wrap.appendChild(button);

    var status = document.createElement('span');
    status.className = 'variant-card__status';
    status.setAttribute('role', 'status');
    wrap.appendChild(status);

    return wrap;
  }

  function buildCard(originalItem, product, group, grid, index) {
    var clone = originalItem.cloneNode(true);
    clone.setAttribute(PROCESSED, 'true');
    clone.setAttribute('data-variant-card', '');
    clone.setAttribute('data-product-handle', product.handle);
    clone.setAttribute('data-option1', group.value);
    clone.removeAttribute('data-cascade');

    uniquifyIds(clone, product.id + '-' + index);
    setQuickAddTarget(clone, product, group.representative);
    setCardLinks(clone, product, group.representative);
    setCardTitle(clone, product, group.representative);
    setCardImage(clone, product, group.representative);
    setCardPrice(clone, group.representative, grid);

    /*
     * A group only exists when it has stock, so a "Sold out" badge cloned from the original
     * card is stale by definition. Relabel it as the sale badge when this variant is
     * discounted, otherwise drop it - leaving the cloned text in place would show
     * "Sold out" on a card that is in stock and buyable.
     */
    var badge = clone.querySelector('.card__badge .badge');
    if (badge) {
      var variant = group.representative;
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        badge.textContent = grid.getAttribute('data-sale-label') || 'Sale';
      } else {
        badge.remove();
      }
    }

    /*
     * Add-to-cart button is intentionally NOT injected right now: appended into
     * .card__content it landed over the card image instead of below it. The button
     * builder and its delegated click handler are left in place, so re-enabling is a
     * one-line change once the card layout has somewhere sensible to put it.
     */
    if (grid.hasAttribute('data-variant-cards-add-to-cart')) {
      var content = clone.querySelector('.card__content') || clone.querySelector('.card-wrapper');
      if (content) content.appendChild(buildAddButton(product, group.representative, grid));
    }

    return clone;
  }

  function splitCard(item, grid) {
    if (item.hasAttribute(PROCESSED) || inFlightCards.has(item)) return;

    var link = item.querySelector('a[href*="/products/"]');
    if (!link) {
      item.setAttribute(PROCESSED, 'true');
      return;
    }

    var handle = (link.getAttribute('href').match(/\/products\/([^?#/]+)/) || [])[1];
    if (!handle) {
      item.setAttribute(PROCESSED, 'true');
      return;
    }

    inFlightCards.add(item);

    fetchProduct(handle)
      .then(function (product) {
        // The grid may have been re-rendered by a filter/sort while this was in flight.
        if (!item.isConnected) return;

        if (!product || !product.variants || product.variants.length <= 1) {
          item.setAttribute(PROCESSED, 'true');
          return;
        }

        // Option 1 isn't the flavor axis - leave the standard single product card alone.
        if (!isSplittable(product, grid)) {
          item.setAttribute(PROCESSED, 'true');
          return;
        }

        var groups = groupByFirstOption(product);

        // Nothing to split into: one group behaves exactly like a normal product card.
        if (groups.length <= 1) {
          item.setAttribute(PROCESSED, 'true');
          return;
        }

        var fragment = document.createDocumentFragment();
        groups.forEach(function (group, index) {
          fragment.appendChild(buildCard(item, product, group, grid, index));
        });

        item.replaceWith(fragment);
      })
      .catch(function () {
        // Leave the original card in place - a failed fetch should never blank the grid.
        if (item.isConnected) item.setAttribute(PROCESSED, 'true');
      })
      .finally(function () {
        inFlightCards.delete(item);
      });
  }

  function fetchProduct(handle) {
    if (productCache[handle]) return productCache[handle];
    productCache[handle] = fetch(window.Shopify.routes.root + 'products/' + handle + '.js')
      .then(function (response) {
        if (!response.ok) throw new Error('product fetch failed');
        return response.json();
      })
      .catch(function (error) {
        delete productCache[handle];
        throw error;
      });
    return productCache[handle];
  }

  /* Lazy: only fetch a product's variants once its card nears the viewport, so a 36-product
     collection doesn't fire 36 requests during initial paint and hurt LCP/INP. */
  var cardObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          function (entries, observer) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              var item = entry.target.closest('.grid__item') || entry.target;
              var grid = entry.target.closest('[data-variant-cards]');
              if (grid) splitCard(item, grid);
            });
          },
          { rootMargin: '400px 0px' }
        )
      : null;

  /*
   * This grid styles its .grid__item wrappers `display: contents`, which means they
   * generate no layout box at all - an IntersectionObserver watching them would never
   * report an intersection and nothing would ever load. Watch the card inside the wrapper,
   * which does have a box, and map back to the .grid__item when it fires.
   */
  function observationTarget(item) {
    return item.querySelector('.card-wrapper, .card, .lg-tile') || item;
  }

  function processGrid(grid) {
    var items = grid.querySelectorAll('.grid__item:not([' + PROCESSED + '])');
    for (var i = 0; i < items.length; i++) {
      if (cardObserver) cardObserver.observe(observationTarget(items[i]));
      else splitCard(items[i], grid);
    }

    // Safety net: if the observer produced nothing (any other zero-box styling, or a card
    // shape without a recognised inner wrapper), split directly rather than silently
    // leaving the collection unsplit.
    if (!cardObserver) return;
    window.setTimeout(function () {
      var stuck = grid.querySelectorAll('.grid__item:not([' + PROCESSED + '])');
      for (var j = 0; j < stuck.length; j++) {
        if (!inFlightCards.has(stuck[j])) splitCard(stuck[j], grid);
      }
    }, 1500);
  }

  function observeGrid(grid) {
    processGrid(grid);

    // Filtering, sorting and pagination all replace the grid's children. Watching the DOM
    // is more durable than hooking whichever internal event the theme's facets code uses.
    if (!window.MutationObserver || grid.hasAttribute('data-variant-cards-observed')) return;
    grid.setAttribute('data-variant-cards-observed', 'true');

    var queued = false;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        processGrid(grid);
      });
    }).observe(grid, { childList: true });
  }

  function init() {
    var grids = document.querySelectorAll('[data-variant-cards]');
    for (var i = 0; i < grids.length; i++) observeGrid(grids[i]);
  }

  /* ---------------------------------------------------------------------------
   * Add to cart straight from a card.
   *
   * Mirrors assets/product-form.js exactly - same `sections`/`sections_url` payload, same
   * PUB_SUB_EVENTS.cartUpdate publish, same cart.renderContents(response) - so the drawer
   * opens and the cart bubble updates identically to a PDP add. Shopify's Web Pixels emit
   * product_added_to_cart for /cart/add.js on their own, so analytics needs no extra call.
   * ------------------------------------------------------------------------- */
  function cartElement() {
    return document.querySelector('cart-notification') || document.querySelector('cart-drawer');
  }

  function setStatus(button, message) {
    var status = button.parentElement && button.parentElement.querySelector('.variant-card__status');
    if (status) status.textContent = message || '';
  }

  function addToCart(button) {
    // Guards the double-click case: the flag is set synchronously, before any await.
    if (button.hasAttribute('data-loading')) return;

    var grid = button.closest('[data-variant-cards]');
    var card = button.closest('[data-variant-card]');
    var variantId = button.getAttribute('data-variant-id');
    if (!grid || !variantId) return;

    button.setAttribute('data-loading', 'true');
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');
    setStatus(button, '');

    var handle = card && card.getAttribute('data-product-handle');

    // Re-check real stock rather than trusting the rendered button, which may be stale if
    // the card has been sitting on screen since before the item sold out.
    var verify = handle
      ? fetchProduct(handle).then(function (product) {
          var match = product.variants.filter(function (v) {
            return String(v.id) === String(variantId);
          })[0];
          return !!(match && match.available);
        })
      : Promise.resolve(true);

    verify
      .then(function (available) {
        if (!available) {
          setStatus(button, grid.getAttribute('data-sold-out-label') || 'Sold out');
          button.setAttribute('disabled', 'disabled');
          return null;
        }

        var cart = cartElement();
        var formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');

        if (cart) {
          formData.append(
            'sections',
            cart.getSectionsToRender().map(function (section) {
              return section.id;
            })
          );
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }

        var config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: {} };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        return fetch(window.Shopify.routes.root + 'cart/add.js', config).then(function (response) {
          return response.json();
        });
      })
      .then(function (response) {
        if (!response) return;

        if (response.status) {
          setStatus(button, response.description || response.message || 'Could not add to cart');
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'variant-cards',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
          }
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'variant-cards',
            productVariantId: variantId,
            cartData: response,
          });
        }

        var cart = cartElement();
        if (cart) {
          if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          cart.renderContents(response);
        } else {
          window.location = window.routes.cart_url;
        }
      })
      .catch(function () {
        setStatus(button, 'Could not add to cart. Please try again.');
      })
      .finally(function () {
        button.removeAttribute('data-loading');
        button.removeAttribute('aria-disabled');
        button.classList.remove('loading');
      });
  }

  // Delegated from the document so it survives every grid re-render; per-button listeners
  // would be lost as soon as filtering replaced the cards.
  document.addEventListener('click', function (event) {
    var button = event.target.closest ? event.target.closest('[data-variant-card-add]') : null;
    if (!button) return;
    event.preventDefault();
    addToCart(button);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Section re-render in the theme editor.
  document.addEventListener('shopify:section:load', init);
})();
